/* eslint-disable function-paren-newline */
// @flow
import * as React from 'react';
import styled from 'styled-components';

import SegmentCategorySelector from './SegmentCategorySelector';
import type { GlobalSegmentCategory, SegmentCategory } from './types';

const SegmentCateogrySelectorWrapper = styled.div`
  width: ${props => props.width};
`;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
  max-width: 1110px;
  margin: 0 auto;
  position: relative;
`;

const getAvailableSegmentCategories = (
  segmentCategories: Array<GlobalSegmentCategory>,
  clientSegmentCategories: Array<SegmentCategory>,
): Array<GlobalSegmentCategory> =>
  segmentCategories.filter(category =>
    clientSegmentCategories.every(
      clientSegmentCategory => category.name !== clientSegmentCategory.name,
    ),
  );

export const hasSegments = (clientSegmentCategory: SegmentCategory): boolean =>
  clientSegmentCategory !== undefined && clientSegmentCategory.segments.length > 0;

/**
 * Contains stages in setting up first segment category.
 */
export const CATEGORY_SETUP_STAGES = {
  INITIAL: 'INITIAL',
  SETUP: 'SETUP',
  FIRST_ADDED: 'FIRST_ADDED',
  COMPLETED: 'COMPLETED',
};

/**
 * Contains explainer and infoTip text corresponding to category set up stage.
 */
export const CATEGORY_SETUP_INFO = {
  INITIAL: {
    explainer: (category?: string) => {
      if (category) return false;
      return `You don't have any tags set up yet. To get started using tags, first tell us which
      business area you would like to track in more detail?`;
    },
    infoTip: `For Example: if you're an online retailer, you may want to track your sales using the business
    area 'Channel'.`,
  },
  SETUP: {
    explainer: (category?: string) =>
      `Next step is to add tags so you can track your revenue or expenses by ${category || ''}.`,
    infoTip: `For Example: You could add a state or province such as California, Illinois or Ontario.
    You could also add a city such as San Francisco, Chicago or Toronto.`,
  },
  FIRST_ADDED: {
    explainer: (
      category?: string,
    ) => `You've added your first business area and tag! You can continue adding more ${
      category || ''
    }
    tags or add another business area to track (you can add up to three).`,
    infoTip: '',
  },
  COMPLETED: {},
};

/**
 * Returns the stage at which the client is at in setting up their very first segment category.
 * The stage provides context to the SegmentCategoryManager for displaying explanation text
 * to assist the client in their set up process.
 * @param {number} numberOfCategories - client categories
 * @param {number} numberOfCategoriesWithSegments - client categories with assigned segments
 * @return {string} describes the category set up stage
 */
export const getCategorySetupStage = (
  numberOfCategories: number,
  numberOfCategoriesWithSegments: number,
) => {
  if (numberOfCategories === 0) return CATEGORY_SETUP_STAGES.INITIAL;
  if (numberOfCategories === 1 && numberOfCategoriesWithSegments === 0) {
    return CATEGORY_SETUP_STAGES.SETUP;
  }
  if (numberOfCategories === 1 && numberOfCategoriesWithSegments === 1) {
    return CATEGORY_SETUP_STAGES.FIRST_ADDED;
  }
  return CATEGORY_SETUP_STAGES.COMPLETED;
};

type SegmentCategoryManagerProps = {
  clientId: string,
  segmentCategories: Array<GlobalSegmentCategory>,
  clientSegmentCategories: Array<SegmentCategory>,
};
export default function SegmentCategoryManager({
  clientId,
  segmentCategories,
  clientSegmentCategories,
}: SegmentCategoryManagerProps) {
  const availableCategories = getAvailableSegmentCategories(
    segmentCategories,
    clientSegmentCategories,
  );
  const clientCategoriesWithSegments = clientSegmentCategories.filter(category =>
    hasSegments(category),
  );
  const numberOfColumns = Math.min(clientSegmentCategories.length + 1, 3);
  const sortedClientCategories = clientSegmentCategories
    .slice()
    .sort((a, b) => Number(a.id) - Number(b.id));
  const setupStage = getCategorySetupStage(
    clientSegmentCategories.length,
    clientCategoriesWithSegments.length,
  );
  const getCategoryData = (index: number) => ({
    clientId,
    availableCategories,
    segmentCategory: sortedClientCategories[index] || null,
  });

  return (
    <StyledContainer>
      {(() => {
        if (setupStage === CATEGORY_SETUP_STAGES.INITIAL) {
          return (
            <SegmentCateogrySelectorWrapper width="66%">
              <SegmentCategorySelector
                explainerText={() => CATEGORY_SETUP_INFO[setupStage].explainer()}
                tipText={CATEGORY_SETUP_INFO[setupStage].infoTip}
                showEditor
                categoryData={getCategoryData(-1)}
                isInitialEditor
              />
            </SegmentCateogrySelectorWrapper>
          );
        }
        if (
          setupStage === CATEGORY_SETUP_STAGES.SETUP ||
          setupStage === CATEGORY_SETUP_STAGES.FIRST_ADDED
        ) {
          return Array.from(Array(numberOfColumns).keys()).map(index => (
            <SegmentCateogrySelectorWrapper
              width={index === 0 ? '33%' : '66%'}
              key={`${setupStage}-${index}`}
            >
              <SegmentCategorySelector
                explainerText={() => {
                  if (index !== 1) return false;
                  const { name } = sortedClientCategories[index - 1];
                  return CATEGORY_SETUP_INFO[setupStage].explainer(name);
                }}
                tipText={index === 1 ? CATEGORY_SETUP_INFO[setupStage].infoTip : ''}
                showEditor={
                  (setupStage === CATEGORY_SETUP_STAGES.SETUP && index !== 1) ||
                  setupStage === CATEGORY_SETUP_STAGES.FIRST_ADDED
                }
                categoryData={getCategoryData(index)}
              />
            </SegmentCateogrySelectorWrapper>
          ));
        }
        return Array.from(Array(numberOfColumns).keys()).map(index => (
          <SegmentCateogrySelectorWrapper width="33%" key={`${setupStage}-${index}`}>
            <SegmentCategorySelector showEditor categoryData={getCategoryData(index)} />
          </SegmentCateogrySelectorWrapper>
        ));
      })()}
    </StyledContainer>
  );
}
